import { FC, useState } from 'react';
import { IonButton, IonList, IonItem } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { BeeChatState } from './BeeChatState';
import { IBeaConversationState } from './IBeaConversationState';
import { IChat } from './BeeChatClient';
import { ChevronRightIcon, CommentPlusIcon } from '../theme/Icons';
import { SearchBar } from '../components/SearchBar';
import dayjs from 'dayjs';

const useStyles = createUseStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },

    listContainer: {
        flex: 1,
        overflowY: 'auto'
    },

    conversationItem: {
        '--padding-start': '16px',
        '--padding-end': '16px',
        '--inner-padding-end': '0',
        cursor: 'pointer'
    },

    itemContent: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: '12px 0',
        gap: 12
    },

    summaryContainer: {
        flex: 1,
        minWidth: 0
    },

    title: {
        fontSize: 15,
        fontWeight: 500,
        color: 'var(--ion-color-dark)',
        margin: 0,
        lineHeight: 1.4,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },

    summary: {
        fontSize: 13,
        color: 'var(--ion-color-medium)',
        margin: 0,
        lineHeight: 1.4,
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },

    timestamp: {
        fontSize: 13,
        color: 'var(--ion-color-medium)',
        flexShrink: 0,
        marginLeft: 8
    },

    chevronWrapper: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0
    },

    buttonContainer: {
        padding: 6,
        borderTop: '1px solid var(--ion-color-step-100)',
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 'var(--ion-safe-area-bottom, 0px)'
    },

    emptyState: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
        textAlign: 'center'
    },

    emptyStateText: {
        fontSize: 16,
        color: 'var(--ion-color-medium)',
        marginBottom: 24
    },

    noResults: {
        padding: 32,
        textAlign: 'center',
        color: 'var(--ion-color-medium)',
        fontSize: 15
    }
});

interface ConversationListViewProps {
    'data-id'?: string;
    /** The conversations listed: family Bea's unless a caller brings its own (agency Bea). */
    chatState?: IBeaConversationState;
}

export const ConversationListView: FC<ConversationListViewProps> = ({ 'data-id': dataId, chatState: givenChatState }) => {
    const classes = useStyles();

    const familyChatState = useInstance(BeeChatState);
    const chatState: IBeaConversationState = givenChatState ?? familyChatState;
    useSubscription(() => chatState);

    const { chats } = chatState.state;
    const [searchQuery, setSearchQuery] = useState('');

    const filterChats = (allChats: IChat[], query: string): IChat[] => {
        if (!query.trim()) {
            return allChats;
        }
        const normalizedQuery = query.toLowerCase();
        return allChats.filter(chat =>
            chatState.getTitle(chat).toLowerCase().includes(normalizedQuery)
            || chatState.getSummary(chat).text.toLowerCase().includes(normalizedQuery)
            || chat.messages.some(msg => msg.content.toLowerCase().includes(normalizedQuery))
        );
    };

    const filteredChats = filterChats(chats, searchQuery);

    const formatTimestamp = (lastMessageAt: string): string => {
        const date = dayjs(lastMessageAt);
        const now = dayjs();

        if (date.isSame(now, 'day')) {
            return date.format('h:mma');
        }
        if (date.isSame(now.subtract(1, 'day'), 'day')) {
            return 'Yesterday';
        }
        if (date.isSame(now, 'year')) {
            return date.format('MMM D');
        }
        return date.format('MMM D, YYYY');
    };

    const handleSelectChat = (chatId: string) => {
        chatState.selectChat(chatId);
    };

    const handleStartNewChat = () => {
        chatState.startNewConversation();
    };

    const renderChatRow = (chat: IChat) => {
        const summary = chatState.getSummary(chat);
        return (
            <IonItem
                key={chat.chatId}
                className={classes.conversationItem}
                lines="full"
                button
                detail={false}
                onClick={() => handleSelectChat(chat.chatId)}
                data-id={`bea-conversation-row-${chat.chatId}`}
            >
                <div className={classes.itemContent}>
                    <div className={classes.summaryContainer}>
                        <p className={classes.title} data-id="bea-conversation-title">
                            {chatState.getTitle(chat)}
                        </p>
                        <p className={summary.isPersonName ? `${classes.summary} mp-mask` : classes.summary}>
                            {summary.text}
                        </p>
                    </div>
                    <span className={classes.timestamp}>
                        {formatTimestamp(chat.lastMessageAt)}
                    </span>
                    <div className={classes.chevronWrapper}>
                        <ChevronRightIcon color="medium" size="small" />
                    </div>
                </div>
            </IonItem>
        );
    };

    return (
        <div className={classes.container} data-id={dataId || 'bea-conversation-list'}>
            {chats.length > 0 && (
                <SearchBar
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                    data-id="bea-conversation-search"
                />
            )}

            {chats.length > 0 ? (
                filteredChats.length > 0 ? (
                    <div className={classes.listContainer}>
                        <IonList>
                            {filteredChats.map(chat => renderChatRow(chat))}
                        </IonList>
                    </div>
                ) : (
                    <div className={classes.noResults}>
                        No conversations match your search
                    </div>
                )
            ) : (
                <div className={classes.emptyState}>
                    <p className={classes.emptyStateText}>
                        No conversations yet. Start a new chat with Bea!
                    </p>
                </div>
            )}

            <div className={classes.buttonContainer}>
                <IonButton
                    color="primary"
                    onClick={handleStartNewChat}
                    data-id="bea-start-new-chat-button"
                >
                    <CommentPlusIcon color="light" size="large" />
                    &nbsp;&nbsp;Start a New Chat
                </IonButton>
            </div>
        </div>
    );
};
